<template>
  <button
    class="dropdown-item"
    v-on:click="openModal = allowCreation"
    v-bind="$attrs"
    :disabled="!allowCreation || undefined"
    :title="allowTitle"
  >
    <span class="icon">
      <i class="icon-warning"></i>
    </span>
    Create using the old format
  </button>

  <Modal
    v-if="allowCreation"
    v-model="openModal"
    :title="modalTitle"
    is-large
  >
    <section class="notification is-warning">
      <p>
        This form creates worker versions using the
        <a
          href="https://doc.teklia.com/base-worker/contents/implem/configure/yaml/"
          target="_blank"
          >deprecated configuration format</a
        >. Support for this format will be removed in a future release.
      </p>
      <p>
        To import a worker version that uses the
        <a
          href="https://doc.teklia.com/arkindex_worker_configuration/"
          target="_blank"
          >new configuration format</a
        >, use the <strong>Import</strong> button instead.
      </p>
    </section>
    <form
      class="form"
      v-on:submit.prevent="create"
    >
      <div class="field">
        <label class="label">Docker image reference</label>
        <div class="control">
          <input
            autofocus
            class="input"
            :class="{ 'is-danger': fieldErrors.docker_image_iid }"
            type="text"
            placeholder="…"
            maxlength="512"
            v-model.trim="payload.docker_image_iid"
            :disabled="loading || undefined"
          />
        </div>
        <p
          v-if="fieldErrors.docker_image_iid"
          class="help is-danger"
        >
          {{ fieldErrors.docker_image_iid.join(', ') }}
        </p>
      </div>

      <div class="field">
        <label class="label">
          JSON configuration
          <div class="dropdown is-hoverable">
            <div class="dropdown-trigger">
              <i class="icon-help has-text-info"></i>
            </div>
            <div class="dropdown-menu">
              <div class="message message-body help-message-width">
                <p>You can define here any JSON configuration for this version.</p>
                <p>
                  If you used
                  <a
                    href="https://pypi.org/project/arkindex-base-worker/"
                    target="_blank"
                    >arkindex-base-worker</a
                  >
                  to create your image, this value will be accessible as
                  <samp class="tag is-light is-size-6">self.config</samp> within the worker class.
                </p>
                <p>
                  Please refer to the
                  <a
                    href="https://doc.arkindex.org"
                    target="_blank"
                    >documentation</a
                  >
                  for more information.
                </p>
              </div>
            </div>
          </div>
        </label>
        <div class="control">
          <textarea
            class="textarea is-family-monospace"
            :class="{ 'is-danger': Boolean(JSONError || fieldErrors.configuration) }"
            v-model="configurationString"
            :disabled="loading || undefined"
          ></textarea>
        </div>
        <p
          v-if="JSONError"
          class="help is-danger"
        >
          {{ JSONError }}
        </p>
        <p
          v-if="fieldErrors.configuration"
          class="help is-danger"
        >
          {{ fieldErrors.configuration.join(', ') }}
        </p>
      </div>

      <div class="field">
        <label class="label">Model usage</label>
        <div class="control">
          <div
            class="select"
            :class="{ 'is-danger': fieldErrors.model_usage }"
          >
            <select
              v-model="payload.model_usage"
              :disabled="loading || undefined"
            >
              <option
                v-for="(value, key) in FeatureUsage"
                :key="key"
                :value="value"
                :title="key"
              >
                {{ key }}
              </option>
            </select>
          </div>
        </div>
        <p
          v-if="fieldErrors.model_usage"
          class="help is-danger"
        >
          {{ fieldErrors.model_usage.join(', ') }}
        </p>
      </div>

      <div class="field">
        <label class="label">GPU usage</label>
        <div class="control">
          <div
            class="select"
            :class="{ 'is-danger': fieldErrors.gpu_usage }"
          >
            <select
              v-model="payload.gpu_usage"
              :disabled="loading || undefined"
            >
              <option
                v-for="(value, key) in FeatureUsage"
                :key="key"
                :value="value"
                :title="key"
              >
                {{ key }}
              </option>
            </select>
          </div>
        </div>
        <p
          v-if="fieldErrors.gpu_usage"
          class="help is-danger"
        >
          {{ fieldErrors.gpu_usage.join(', ') }}
        </p>
      </div>
    </form>

    <template v-slot:footer="{ close }">
      <button
        class="button"
        v-on:click="close"
      >
        Cancel
      </button>
      <button
        type="submit"
        v-on:click="create"
        class="button is-primary"
        :class="{ 'is-loading': loading }"
        :disabled="Boolean(JSONError) || undefined"
        :title="saveTitle"
      >
        Create
      </button>
    </template>
  </Modal>
</template>

<script lang="ts">
import { isAxiosError } from 'axios'
import { mapActions, mapState } from 'pinia'
import { defineComponent } from 'vue'

import type { CreateWorkerVersionPayload } from '@/api'
import Modal from '@/components/Modal.vue'
import { FeatureUsage, WorkerVersionState } from '@/enums'
import { ensureArray, errorParser } from '@/helpers'
import { useAuthStore, useNotificationStore, useWorkerStore } from '@/stores'
import type { Worker } from '@/types/worker'

export default defineComponent({
  emits: ['created'],
  components: {
    Modal,
  },
  props: {
    workerId: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    FeatureUsage,
    openModal: false,
    configurationString: '{}',
    JSONError: null as string | null,
    payload: {
      docker_image_iid: '',
      configuration: {},
      model_usage: FeatureUsage.Disabled,
      gpu_usage: FeatureUsage.Disabled,
    } as CreateWorkerVersionPayload,
    loading: false,
    fieldErrors: {} as Partial<Record<keyof CreateWorkerVersionPayload, string[]>>,
  }),
  computed: {
    ...mapState(useAuthStore, ['user']),
    ...mapState(useWorkerStore, ['workers']),
    worker(): Worker | null {
      return this.workers?.[this.workerId] ?? null
    },
    allowCreation(): boolean {
      if (!this.worker || !this.user) return false
      return (
        this.worker?.repository_url === null &&
        (this.user.is_admin || this.user.can_create_worker_version)
      )
    },
    allowTitle(): string {
      if (this.worker === null) return ''
      if (this.worker?.repository_url !== null)
        return 'Versions cannot be created on workers linked to a repository'
      if (!this.user || !(this.user.is_admin || this.user.can_create_worker_version))
        return 'You do not have the required permission to create worker versions'
      return 'Create a version'
    },
    modalTitle() {
      return this.worker ? `Create a version for worker "${this.worker.name}"` : 'Create a version'
    },
    saveTitle() {
      return this.JSONError ? 'Please enter a valid configuration' : 'Create a new version'
    },
  },
  methods: {
    ...mapActions(useWorkerStore, ['createWorkerVersion', 'getWorker']),
    ...mapActions(useNotificationStore, ['notify']),
    async create() {
      if (this.JSONError) return
      this.loading = true
      this.fieldErrors = {}
      try {
        const cleanPayload = { ...this.payload }
        if (cleanPayload.docker_image_iid) {
          // Automatically mark the worker as available when a docker image is set
          cleanPayload.state = WorkerVersionState.Available
        } else {
          delete cleanPayload.docker_image_iid
        }
        await this.createWorkerVersion(this.workerId, cleanPayload)
        this.notify({ type: 'info', text: 'Worker version created successfully.' })
        this.openModal = false
        this.payload = {
          docker_image_iid: '',
          configuration: {},
          model_usage: FeatureUsage.Disabled,
          gpu_usage: FeatureUsage.Disabled,
        }
        this.configurationString = '{}'
        this.$emit('created')
      } catch (err) {
        this.notify({ type: 'error', text: errorParser(err) })
        if (isAxiosError(err) && err?.response?.data) {
          this.fieldErrors = Object.fromEntries(
            Object.entries(err?.response?.data).map(
              ([k, v]: [string, unknown]): [string, string[]] => [
                k,
                /*
                 * Ensure every error message is an array of strings,
                 * first by converting to an array, then by converting anything that isn't a string in the array
                 * to its JSON representation, to get some basic handling of complex error messages for the version's configuration
                 */
                ensureArray(v).map((message: unknown): string =>
                  typeof message === 'string' ? message : JSON.stringify(message),
                ),
              ],
            ),
          )
        }
      } finally {
        this.loading = false
      }
    },
  },
  watch: {
    configurationString(newValue) {
      this.JSONError = null
      try {
        const data = JSON.parse(newValue)
        // Update the payload value directly when checking errors
        if (typeof data === 'string' || Array.isArray(data)) {
          this.JSONError = 'Configuration JSON body must be an object'
        } else {
          this.payload.configuration = data
        }
      } catch (e) {
        if (e instanceof SyntaxError) this.JSONError = e.message
        else throw e
      }
    },
  },
})
</script>

<style scoped>
.help-message-width {
  width: min(50vw, 100rem);
}
</style>
